import { useState, useEffect } from 'react';
import { Plus, Building2, FileText, ArrowRight, Loader2, CheckCircle, List } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface USPTOTaskGuideProps {
  onAddApplication: (appNumber: string) => Promise<void>;
  onCloseWizard: () => void;
  onNavigateToUSPTO: () => void;
  sessionApplications?: any[];
}

export function USPTOTaskGuide({ onAddApplication, onCloseWizard, onNavigateToUSPTO, sessionApplications = [] }: USPTOTaskGuideProps) {
  const [mode, setMode] = useState<'select' | 'new'>('select');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [appNumber, setAppNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAppId, setLoadingAppId] = useState<string | null>(null);

  // If there are no existing applications, default to "new" mode
  useEffect(() => {
    if (sessionApplications.length === 0) {
      setMode('new');
    }
  }, [sessionApplications]);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { docType, docId, success, error: jobError } = event.detail;

      // Only handle if it's the application we're loading
      if (docType === 'application' && docId === loadingAppId) {
        setIsLoading(false);
        if (success) {
          setIsComplete(true);
          setError(null);
          setAppNumber(docId); // Make sure appNumber is set to the loaded ID

          // Add to session context (same pattern as ExaminationPage)
          const storedContext = localStorage.getItem('session_context');
          const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};
          const currentApps = context.current_applications || [];
          if (!currentApps.includes(docId)) {
            context.current_applications = [...currentApps, docId];
            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));
          }
        } else {
          setError(jobError || 'Failed to load application');
          setLoadingAppId(null);
        }
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [loadingAppId]);

  const handleAddApplication = async () => {
    if (!appNumber.trim()) {
      setError('Please enter an application number');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // Clean the app number to get the ID that will be used
      let cleanedId = appNumber.trim()
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/^US/i, '')
        .replace(/[A-Z]\d*$/i, '');

      setLoadingAppId(cleanedId);
      await onAddApplication(appNumber.trim());
      // Don't set isComplete here - wait for job-complete event
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
      setIsLoading(false);
      setLoadingAppId(null);
    }
  };

  const handleSelectExisting = () => {
    if (selectedAppId) {
      setIsComplete(true);
      setAppNumber(selectedAppId);

      // Add to session context (same pattern as ExaminationPage)
      const storedContext = localStorage.getItem('session_context');
      const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};
      const currentApps = context.current_applications || [];
      if (!currentApps.includes(selectedAppId)) {
        context.current_applications = [...currentApps, selectedAppId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    }
  };

  const handleGoToUSPTO = () => {
    onNavigateToUSPTO();
    onCloseWizard();
  };

  const canProceed = mode === 'select' ? selectedAppId : isComplete;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">View USPTO Data</h2>
        <p className="text-gray-600">
          Check prosecution history and application status
        </p>
      </div>

      <div className="space-y-4">
        {/* Step 1: Select or Add Application */}
        <div className={`border-2 rounded-lg p-5 transition-colors ${
          canProceed
            ? 'border-green-200 bg-green-50'
            : 'border-indigo-200 bg-indigo-50'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-8 h-8 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
              canProceed ? 'bg-green-600' : 'bg-indigo-600'
            }`}>
              {canProceed ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                {canProceed ? 'Application Ready!' : 'Select or Add Application'}
              </h3>

              {!canProceed ? (
                <>
                  {/* Mode Toggle */}
                  {sessionApplications.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      <Button
                        variant={mode === 'select' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('select')}
                        className="gap-2"
                      >
                        <List className="w-4 h-4" />
                        Select Existing
                      </Button>
                      <Button
                        variant={mode === 'new' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('new')}
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add New
                      </Button>
                    </div>
                  )}

                  {mode === 'select' ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Select an existing application from your session
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {sessionApplications.map((app) => {
                          const appId = app.application_id || app.application?.applicationNumberText || 'Unknown';
                          const isSelected = selectedAppId === appId;
                          return (
                            <button
                              key={appId}
                              onClick={() => setSelectedAppId(appId)}
                              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                                isSelected
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 bg-white hover:border-indigo-300'
                              }`}
                            >
                              <div className="font-medium text-gray-900">{appId}</div>
                              {app.application?.inventionTitle && (
                                <div className="text-xs text-gray-600 mt-1 truncate">
                                  {app.application.inventionTitle}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <Button
                        onClick={handleSelectExisting}
                        disabled={!selectedAppId}
                        className="gap-2 bg-indigo-600 hover:bg-indigo-700 mt-3"
                        size="sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Use This Application
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Enter a US application number to load its data
                      </p>
                      <div className="space-y-3">
                        <Input
                          type="text"
                          placeholder="16/123,456 or 16123456"
                          value={appNumber}
                          onChange={(e) => setAppNumber(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                              handleAddApplication();
                            }
                          }}
                          disabled={isLoading}
                          className="bg-white"
                        />
                        {error && (
                          <p className="text-sm text-red-600">{error}</p>
                        )}
                        <Button
                          onClick={handleAddApplication}
                          disabled={isLoading || !appNumber.trim()}
                          className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                          size="sm"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading Application...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              Load Application
                            </>
                          )}
                        </Button>
                        <div className="text-xs text-gray-600 bg-white/50 rounded px-3 py-2">
                          <strong>Example:</strong> Enter "16/123,456" or "16123456"
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  Application <strong>{appNumber || selectedAppId}</strong> is ready.
                  Continue to the USPTO Data tab to view details.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: View USPTO Tab */}
        {canProceed && (
          <div className="border-2 border-blue-200 rounded-lg p-5 bg-blue-50">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">View Application Data</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Click below to view detailed application information, status, and history
                </p>
                <Button
                  onClick={handleGoToUSPTO}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Building2 className="w-4 h-4" />
                  Go to USPTO Data
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* What You'll See */}
        {!canProceed && (
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-5 border border-indigo-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              What You'll See
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Application Details:</strong> Filing date, status, assignee, etc.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Prosecution History:</strong> Office actions, responses, and timeline</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span><strong>Documents:</strong> Access to specification, claims, and drawings</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
